import {
  Alert,
  AlertIcon,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useCreatePreassignedInternalLoanTapesFromPreviewMetadataMutation } from 'packages/loanProduct/requests/requests.gql.generated';

type Props = {
  masterLoanTapeIds: string[];
  previewIdentifier: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  disabled?: boolean;
};

export const AssignmentSummaryConfirmButton: React.FC<Props> = ({
  masterLoanTapeIds,
  previewIdentifier,
  reportingDate,
  fileVersion,
  disabled,
}) => {
  const toast = useToast();
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    createPreassignedInternalLoanTapesFromPreviewMetadata,
    { loading },
  ] = useCreatePreassignedInternalLoanTapesFromPreviewMetadataMutation({
    variables: {
      masterLoanTapeIds,
      previewIdentifier,
      reportingDate,
      fileVersion,
    },
  });

  const cancelRef = React.useRef();

  return (
    <>
      <Button
        colorScheme="aurora"
        aria-label="confirm assignment"
        isLoading={loading}
        onClick={onOpen}
        disabled={!!disabled}
      >
        Confirm Assignments
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef.current}
        isOpen={isOpen}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm assignments
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb="10px">
                Are you sure you would like to confirm assignment for the
                following?
              </Text>
              <Alert status="warning" mb="20px" mt="20px">
                <AlertIcon />
                <AlertDescription>
                  Receivable assignments cannot be altered after confirmation.
                </AlertDescription>
              </Alert>
              <Text>
                <strong>Reporting date: </strong>
                {commonDate.fullDateStrToHumanDateString(reportingDate)}
              </Text>
              <Text>
                <strong>File version: </strong>
                {fileVersion}
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef.current} onClick={onClose}>
                Cancel
              </Button>
              <Button
                isLoading={loading}
                colorScheme="green"
                onClick={async () => {
                  try {
                    const {
                      data,
                    } = await createPreassignedInternalLoanTapesFromPreviewMetadata();

                    onClose();

                    if (data) {
                      successToastConfig.title =
                        'Successfully submitted assignments. Please check your email for confirmation.';
                      toast(successToastConfig);
                      history.push('/');
                    }
                  } catch (err: any) {
                    if (!toast.isActive(errorToastId)) {
                      errorToastConfig.title = `Error confirming assignment`;
                      toast(errorToastConfig);
                    }
                    onClose();
                    throw err;
                  }
                }}
                ml={3}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
